/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Stethoscope, Mic, FileArchive, Bot, Bandage, Pill, History, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { getPatientTimeline } from '@/lib/patientTimeline';

const KIND_META = {
  appointment:    { label: 'Visita', Icon: Stethoscope, color: 'text-primary bg-primary/10' },
  voice_note:     { label: 'Nota vocale', Icon: Mic, color: 'text-emerald-600 bg-emerald-500/10' },
  fse_document:   { label: 'Documento FSE', Icon: FileArchive, color: 'text-blue-600 bg-blue-500/10' },
  agentic_alert:  { label: 'Alert IoT', Icon: Bot, color: 'text-amber-600 bg-amber-500/10' },
  wound_case:     { label: 'Wound Care', Icon: Bandage, color: 'text-rose-600 bg-rose-500/10' },
  prescription:   { label: 'Ricetta', Icon: Pill, color: 'text-fuchsia-600 bg-fuchsia-500/10' },
};

const SEVERITY_META = {
  critical: 'bg-red-500/15 text-red-700 dark:text-red-300',
  high:     'bg-red-500/15 text-red-700 dark:text-red-300',
  medium:   'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  low:      'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

const formatDT = (iso) => {
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso || ''; }
};

/**
 * Right-side drawer showing every event (appointments, notes, FSE, alerts,
 * wound care) recorded on the platform for a given patient.
 *
 * Props
 *   patientId    string | null    when null the sheet stays closed
 *   patientLabel string           optional pre-known name (shows immediately)
 *   open         bool             external open state
 *   onOpenChange (bool) => void
 */
export const PatientTimelineDrawer = ({ patientId, patientLabel, open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await getPatientTimeline(patientId);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare la cronologia');
      setData(null);
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { if (open && patientId) load(); }, [open, patientId, load]);

  const p = data?.patient || {};
  const shownName = p.full_name || patientLabel || 'Paziente';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[520px] overflow-y-auto" data-testid="patient-timeline-drawer">
        <SheetHeader className="pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <SheetTitle className="font-display">Cronologia paziente</SheetTitle>
            <Button variant="ghost" size="sm" onClick={load} className="ml-auto" disabled={loading} data-testid="timeline-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
            </Button>
          </div>
          <SheetDescription>
            <span className="font-semibold text-foreground">{shownName}</span>
            {p.email && <span className="ml-1 text-xs">· {p.email}</span>}
          </SheetDescription>
          {patientId && (
            <Button asChild variant="outline" size="sm" className="mt-2 w-full sm:w-auto rounded-xl" data-testid="timeline-send-report">
              <Link to={`/referti?patient=${encodeURIComponent(patientId)}`}><Lock className="h-3.5 w-3.5 mr-1" /> Carica / valida referto cifrato</Link>
            </Button>
          )}
        </SheetHeader>

        {loading && (
          <div className="space-y-3 mt-3">
            {[...Array(6)].map((_, i) => <Skeleton key={`timeline-skel-${i}`} className="h-16 rounded-xl" />)}
          </div>
        )}

        {!loading && data && (
          <>
            {/* Stats bar */}
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="timeline-stats">
              {Object.entries(data.counts || {}).map(([kind, count]) => {
                const meta = KIND_META[kind] || { label: kind };
                return (
                  <Badge key={kind} variant="secondary" className="text-[10.5px]">
                    {meta.label} · {count}
                  </Badge>
                );
              })}
              {(!data.events || data.events.length === 0) && (
                <span className="text-xs text-muted-foreground">Nessun evento registrato.</span>
              )}
            </div>

            {/* Vertical timeline */}
            <ol className="relative mt-4 border-l border-border pl-4 space-y-4">
              {(data.events || []).map((e) => {
                const meta = KIND_META[e.kind] || { label: e.kind, Icon: History, color: 'text-muted-foreground bg-muted' };
                const Icon = meta.Icon;
                return (
                  <li key={`${e.kind}-${e.id}`} className="relative" data-testid={`timeline-item-${e.id}`}>
                    <span className={`absolute -left-[26px] top-0 flex h-5 w-5 items-center justify-center rounded-full ${meta.color}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="rounded-lg border border-border p-3 bg-card">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{e.title}</div>
                          <div className="text-[11px] text-muted-foreground">{formatDT(e.at)} · {meta.label}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {e.severity && (
                            <Badge className={`text-[10px] ${SEVERITY_META[e.severity] || ''}`} variant="secondary">
                              {e.severity}
                            </Badge>
                          )}
                          {e.status && (
                            <Badge variant="outline" className="text-[10px] uppercase">{e.status}</Badge>
                          )}
                        </div>
                      </div>
                      {e.subtitle && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{e.subtitle}</p>
                      )}
                      {e.tags?.length ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {e.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">#{t}</Badge>)}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PatientTimelineDrawer;
